import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { MessageSquare } from 'lucide-react';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { cn } from '@/lib/utils';
import { ConversationList } from '@/components/chat/ConversationList';
import { Thread } from '@/components/chat/Thread';

export const chatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/chat',
  component: () => <ChatPage conversationId={null} />,
});

export const chatConversationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/chat/$conversationId',
  component: ChatConversationPage,
});

function ChatConversationPage(): React.ReactElement {
  const { conversationId } = chatConversationRoute.useParams();
  return <ChatPage conversationId={conversationId} />;
}

// Edge to edge: the list and the thread split the viewport below the header;
// on a phone whichever one the URL points at takes the whole screen.
function ChatPage({ conversationId }: { conversationId: string | null }): React.ReactElement {
  return (
    <AppShell fullBleed>
      <div className="flex min-h-0 flex-1">
        <aside
          className={cn(
            'w-full shrink-0 flex-col border-r border-[hsl(var(--border))]/70 bg-[hsl(var(--card))] md:flex md:w-80 lg:w-96',
            conversationId ? 'hidden' : 'flex',
          )}
        >
          <ConversationList activeId={conversationId} />
        </aside>
        <section
          className={cn('min-w-0 flex-1 flex-col md:flex', conversationId ? 'flex' : 'hidden')}
          aria-label="Conversation"
        >
          {conversationId ? (
            <Thread key={conversationId} conversationId={conversationId} />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
              <div className="rounded-full bg-[hsl(var(--muted))] p-4 text-[hsl(var(--brand))]">
                <MessageSquare className="size-6" />
              </div>
              <p className="font-medium">Pick a conversation</p>
              <p className="max-w-sm text-sm text-[hsl(var(--muted-foreground))]">
                Messages arrive live on every page of IRIS, and the badge in the sidebar counts what
                you have not read yet.
              </p>
            </div>
          )}
        </section>
      </div>
    </AppShell>
  );
}
